import React, { useState } from "react";
import {
  Modal,
  Box,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  TextField,
  MenuItem,
  makeStyles,
  IconButton,
} from "@material-ui/core";
import { Close, ChevronRight } from "@material-ui/icons";
import { reportCommentAPI, reportPostAPI, reportProfileAPI } from "../../api/userApi";
import { showToast } from "../../utils/toast";

const useStyles = makeStyles(theme => ({
  modalSubscriptionWrapper: {
    // Wrapper for backdrop
  },
  modalTipWrapper: {
    position: "fixed",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    backgroundColor: "#131313",
    padding: "0px",
    width: "500px",
    borderRadius: "12px 12px",
    zIndex: 1401,
    boxShadow: "0px 4px 20px rgba(0,0,0,0.5)",
    [theme.breakpoints.down("sm")]: {
      width: "90%",
    },
  },
  modalTitle: {
    backgroundColor: "#131313",
    color: "#FE5A97",
    textAlign: "center",
    fontSize: "24px",
    padding: "20px 0",
    borderBottom: "1px solid #363636",
  },
  modalContent: {
    backgroundColor: "#131313",
    color: "#fff",
    padding: "24px 40px 40px",
    borderRadius: "12px",
    [theme.breakpoints.down("xs")]: {
      padding: "0 16px 32px",
    },
  },
  modalSubtitle: {
    fontSize: "16px",
    marginBottom: "16px",
    lineHeight: "1.5",
    textAlign: "center",
  },
  modalActions: {
    backgroundColor: "#131313",
    padding: "8px 40px 40px",
    justifyContent: "space-between",
    borderRadius: "0 0 12px 12px",
    [theme.breakpoints.down("xs")]: {
      display: "flex",
      flexDirection: "column-reverse",
      gap: 8,
      padding: "0 16px 32px",
      "& .MuiButton-root": {
        minWidth: "100%",
      }
    },
  },
  cancelButton: {
    color: "#fff",
    padding: "12px 40px",
    maxWidth: "142px",
    fontSize: "20px",
    borderRadius: "6px",
    border: "1px solid #FE5A97",
    textTransform: "capitalize",
    backgroundColor: "transparent",
    "&:hover": {
      backgroundColor: "#FE5A97",
    },
    [theme.breakpoints.down("sm")]: {
      maxWidth: "100px",
      fontSize: "14px",
    },
  },
  sendButton: {
    color: "#fff",
    padding: "12px 40px",
    maxWidth: "240px",
    fontSize: "20p",
    borderRadius: "6px",
    textTransform: "capitalize",
    backgroundColor: "#FE5A97",
    textWrap: "nowrap",
    "&:hover": {
      border: "1px solid #FE5A97",
      backgroundColor: "transparent",
    },
    [theme.breakpoints.down("xs")]: {
      maxWidth: "180px",
      fontSize: "14px",
      padding: "12px 24px",
      "&.MuiButton-root": {
        marginLeft: 0,
      }
    },
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "40px 40px 8px 40px",
    [theme.breakpoints.down("xs")]: {
      padding: "32px 16px 24px",
    },
  },
  headerTitle: {
    color: "#fff",
    fontSize: "24px",
    fontWeight: 700,
  },
  headerTitleStep1: {
    color: "#fff",
    fontSize: "24px",
    fontWeight: 700,
    [theme.breakpoints.down("md")]: {
      fontSize: "20px",
    },
  },
  closeButton: {
    color: "#fff",
    padding: "8px",
  },
  reasonOption: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "16px",
    backgroundColor: "transparent",
    borderBottom: "1px solid #363636",
    cursor: "pointer",
    transition: "all 0.2s",
    "&:hover": {
      backgroundColor: "#252525",
      borderColor: "#FE5A97",
    },
    [theme.breakpoints.down("md")]: {
      padding: "12px 8px",
    },
  },
  reasonOptionLast: {
    borderBottom: "none",
  },
  reasonText: {
    color: "#fff",
    fontSize: "16px",
    flex: 1,
    [theme.breakpoints.down("md")]: {
      fontSize: "14px",
    },
  },
  arrowIcon: {
    color: "#FFFFFF",
    marginLeft: "16px",
    [theme.breakpoints.down("md")]: {
      fontSize: "1em",
      marginLeft: "7px",
    },
  },
  stepTitle: {
    color: "#fff",
    fontSize: "20px",
    fontWeight: 600,
    marginBottom: "20px",
    [theme.breakpoints.down("md")]: {
      fontSize: "16px",
    },
  },
  inputWithBorder: {
    "& .MuiOutlinedInput-notchedOutline": {
      border: "1px solid #747474",
      borderRadius: "8px",
    },
    "& .MuiOutlinedInput-root": {
      backgroundColor: "#1F1F1F",
    },
  },
  menuPaper: {
    backgroundColor: "#1F1F1F",
    border: "1px solid #363636",
    borderRadius: "8px",
    marginTop: "8px",
  },
}));

export interface ReportPostDialogProps {
  open: boolean;
  onClose: () => void;
  targetId: string;
  onReportSubmitted?: () => void;
  typeReport?: "post" | "profile" | "comment"
}

const REPORT_POST_REASONS = [
  "It violates intellectual property",
  "This author is impersonating someone else",
  "Self-injury or disorders promotion",
  "Spam or fraud",
  "Selling or promoting restricted items",
] as const;

const REPORT_PROFILE_REASONS = [
  "This creator is impersonating someone else",
  "Self-injury or disorders promotion",
  "Spam or fraud",
  "Selling or promoting restricted items",
  "Harassment or bullying",
  "Privacy violation"
] as const;

const REPORT_COMMENT_REASONS = [
  "It violates intellectual property",
  "This author is impersonating someone else",
  "Self-injury or disorders promotion",
  "Spam or fraud",
  "Selling or promoting restricted items",
  "Harassment",
] as const;

const ReportPostDialog: React.FC<ReportPostDialogProps> = ({
  open,
  onClose,
  targetId,
  onReportSubmitted,
  typeReport = "post"
}) => {
  const classes = useStyles();
  const [step, setStep] = useState<0 | 1>(0);
  const [reportReason, setReportReason] = useState<string>("");
  const [reportComment, setReportComment] = useState<string>("");
  const [reportSubmitting, setReportSubmitting] = useState(false);

  const REPORT_REASONS = typeReport === "post"
    ? REPORT_POST_REASONS
    : typeReport === "profile"
      ? REPORT_PROFILE_REASONS
      : REPORT_COMMENT_REASONS;
  const reportTargetName = typeReport === "post" ? "post" : typeReport === "profile" ? "user" : "comment"

  const handleClose = () => {
    setStep(0);
    setReportReason("");
    setReportComment("");
    onClose();
  };

  const handleReasonSelect = (reason: string) => {
    setReportReason(reason);
    setStep(1);
  };

  const handleBack = () => {
    setStep(0);
  };

  const submitReport = async () => {
    if (!targetId) return;
    setReportSubmitting(true);
    try {
      if (typeReport === "post") {
        await reportPostAPI(targetId, {
          reason: reportReason,
          comment: reportComment.trim() || undefined,
        });
      }
      else if (typeReport === "profile") {
        await reportProfileAPI(targetId, {
          reason: reportReason,
          comment: reportComment.trim() || undefined,
        });
      }
      else if (typeReport === "comment") {
        await reportCommentAPI(targetId, {
          reason: reportReason,
          comment: reportComment.trim() || undefined,
        });
      }

      showToast("Report submitted", "success");
      handleClose();
      if (onReportSubmitted) {
        onReportSubmitted();
      }
    } catch (e) {
      showToast((e as Error)?.message || "Failed to submit report", "error");
    } finally {
      setReportSubmitting(false);
    }
  };

  return (
    <Modal
      open={open}
      disablePortal
      disableEnforceFocus
      disableScrollLock
      BackdropProps={{
        onClick: handleClose,
        style: {
          backgroundColor: "rgba(0, 0, 0, 0.5)",
        },
      }}
      className={classes.modalSubscriptionWrapper}
    >
      <Box
        className={classes.modalTipWrapper}
        onClick={(e: React.MouseEvent) => {
          e.stopPropagation();
        }}
      >
        {/* Header */}
        <Box className={classes.header}>
          <Typography
            className={
              step === 1 ? classes.headerTitleStep1 : classes.headerTitle
            }
          >
            {step === 1 ? "Submit a report" : `Report ${reportTargetName}`}
          </Typography>
          <IconButton
            onClick={handleClose}
            className={classes.closeButton}
            size="small"
          >
            <Close />
          </IconButton>
        </Box>

        <DialogContent className={classes.modalContent}>
          {step === 0 ? (
            <>
              <Typography className={classes.stepTitle}>
                Why are you reporting this {reportTargetName}?
              </Typography>
              {REPORT_REASONS.map((reason, index) => (
                <Box
                  key={index}
                  className={`${classes.reasonOption} ${index === REPORT_REASONS.length - 1
                    ? classes.reasonOptionLast
                    : ""
                    }`}
                  onClick={() => handleReasonSelect(reason)}
                >
                  <Typography className={classes.reasonText}>
                    {reason}
                  </Typography>
                  <ChevronRight className={classes.arrowIcon} />
                </Box>
              ))}
            </>
          ) : (
            <>
              <Typography className={classes.stepTitle}>
                Why are you reporting this {reportTargetName}?
              </Typography>

              <Box mt={2}>
                <Typography style={{ color: "#fff", marginBottom: 8, fontSize: "14px" }}>
                  Reason:
                </Typography>
                <TextField
                  select
                  fullWidth
                  variant="outlined"
                  value={reportReason}
                  onChange={e => setReportReason(e.target.value)}
                  placeholder="Select the reason..."
                  className={classes.inputWithBorder}
                  SelectProps={{
                    MenuProps: {
                      classes: { paper: classes.menuPaper },
                      anchorOrigin: {
                        vertical: "bottom",
                        horizontal: "left",
                      },
                      transformOrigin: {
                        vertical: "top",
                        horizontal: "left",
                      },
                      getContentAnchorEl: null,
                    },
                  }}
                  InputProps={{
                    inputProps: {
                      style: {
                        padding: "12px",
                      },
                    },
                    style: {
                      color: "#fff",
                      backgroundColor: "#1F1F1F",
                      fontSize: "16px",
                    },
                  }}
                >
                  {REPORT_REASONS.map(reason => (
                    <MenuItem key={reason} value={reason} style={{ color: "#fff" }}>
                      {reason}
                    </MenuItem>
                  ))}
                </TextField>
              </Box>

              <Box mt={2}>
                <Typography style={{ color: "#fff", marginBottom: 8, fontSize: "14px" }}>
                  Your comment:
                </Typography>
                <TextField
                  multiline
                  rows={1}
                  fullWidth
                  variant="outlined"
                  value={reportComment}
                  onChange={e => setReportComment(e.target.value)}
                  placeholder=""
                  className={classes.inputWithBorder}
                  InputProps={{
                    style: {
                      color: "#fff",
                      backgroundColor: "#1F1F1F",
                      padding: "12px",
                      fontSize: "16px",
                    },
                  }}
                  InputLabelProps={{ style: { color: "#fff" } }}
                />
              </Box>
            </>
          )}
        </DialogContent>

        {step === 1 && (
          <DialogActions className={classes.modalActions}>
            <Button onClick={handleBack} className={classes.cancelButton}>
              Cancel
            </Button>
            <Button
              onClick={submitReport}
              className={classes.sendButton}
              disabled={reportSubmitting || !reportReason}
            >
              {reportSubmitting ? "Sending…" : "Submit"}
            </Button>
          </DialogActions>
        )}
      </Box>
    </Modal>
  );
};

export default ReportPostDialog;
